import { CSSProperties, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { mainButton } from '@telegram-apps/sdk-react';

import { useBackButtonToCloseModal } from '@/hooks/useBackButtonToCloseModal.tsx';
import { BodyType } from '@/enums/apiEnums.ts';
import { useProfileStore } from '@/store/useProfileStore.ts';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import { helpers } from '@/helpers.ts';
import { Toast } from '@/components/Toast/Toast.tsx';
import { MessageTypesUI } from '@/enums/viewEnums.ts';
import {
  containerStyle,
  inputContainerStyle,
  listItemStyle,
  scrollableListStyle,
  selectedItemMarkStyle,
  selectedItemStyle,
  unselectedItemStyle
} from '@/pages/Profile/Edit/EditEmployment/styles.ts';
import {
  iconCircleStyle,
  innerCircleStyle,
  svgContainerStyle,
  titleStyle
} from '@/pages/Profile/Edit/styles.ts';
import BodyShape from '@/components/common/svg/shapes/BodyShape.tsx';

export const getBodyTypes = (t: TFunction): Record<BodyType, string> => ({
  [BodyType.BODY_TYPE_UNSPECIFIED]: t('status_unspecified'),
  [BodyType.BODY_TYPE_THIN]: t('body_type.thin'),
  [BodyType.BODY_TYPE_AVERAGE]: t('body_type.average'),
  [BodyType.BODY_TYPE_ATHLETIC]: t('body_type.athletic'),
  [BodyType.BODY_TYPE_LARGE]: t('body_type.large'),
  [BodyType.BODY_TYPE_EXTRA_POUNDS]: t('body_type.extra_pounds')
});

interface EditBodyTypePopupProps {
  handleClose: () => void;
}

const EditBodyTypePopup: FC<EditBodyTypePopupProps> = ({ handleClose }) => {
  useBackButtonToCloseModal(handleClose, false);
  
  const { t } = useTranslation();
  const { aboutMe, updateProfile } = useProfileStore();
  const bodyTypes = getBodyTypes(t);

  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [selectedBodyType, setSelectedBodyType] = useState<BodyType>(
    aboutMe?.profile.body_type ?? BodyType.BODY_TYPE_UNSPECIFIED
  );

  const selectBodyType = (value: BodyType) => {
    setIsEdited(true);
    setSelectedBodyType(value);
  };

  const submitBodyType = async () => {
    mainButton.setParams({ isLoaderVisible: true, isEnabled: false });

    try {
      await updateProfile({ body_type: selectedBodyType });
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_SUCCESS,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_SUCCESS} />
      });
      handleClose();
    } catch {
      helpers.toast({
        id: MessageTypesUI.TYPE_SYSTEM_ERROR,
        content: <Toast type={MessageTypesUI.TYPE_SYSTEM_ERROR} />
      });
    } finally {
      mainButton.setParams({
        isLoaderVisible: false,
        isVisible: false,
        isEnabled: true
      });
    }
  };

  useEffect(() => {
    mainButton.setParams({
      isVisible: isEdited
    });
  }, [isEdited]);

  useEffect(() => {
    mainButton.setParams({
      isEnabled: true,
      backgroundColor: '#000000',
      text: t('button.save')
    });

    mainButton.onClick(submitBodyType);
    return () => mainButton.offClick(submitBodyType);
  }, [selectedBodyType]);

  return (
    <div style={popupContainerStyle}>
      <div style={svgContainerStyle}>
        <div style={iconCircleStyle}>
          <div style={innerCircleStyle}>
            <BodyShape width={25} height={25} fillColor="#fff" />
          </div>
        </div>
        <div style={titleStyle}>{t('indicate_your_body_type')}</div>
      </div>
      <div style={inputContainerStyle}>
        <InputContent>
          <div style={containerStyle}>
            <div style={scrollableListStyle}>
              {Object.entries(bodyTypes).map(([value, label]) => (
                <div
                  key={value}
                  onClick={() => selectBodyType(value as BodyType)}
                  style={{
                    ...listItemStyle,
                    ...(selectedBodyType === (value as BodyType)
                      ? selectedItemStyle
                      : unselectedItemStyle),
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  {label}
                  {selectedBodyType === (value as BodyType) && (
                    <span style={selectedItemMarkStyle}>✓</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </InputContent>
      </div>
    </div>
  );
};

const popupContainerStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  overflow: 'auto'
};

export default EditBodyTypePopup;
